import { useState, useCallback, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../utils/utils";
import { Input } from "./ui/input";
import Spinner from "./spinner.components";
import CheckIcon from "../assets/svgs/common/check.svg";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";

interface SearchSelectProps {
  options: {
    value: string;
    label: string;
    data?: any;
  }[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  fetcher?: (searchQuery: string) => void;
  fetching?: boolean;
  onSearch?: (searchQuery: string) => void;
  showAllOnEmpty?: boolean;
  maxResultsWhileSearching?: number;
  emptyMessage?: string;
}

export function SearchSelect({
  options,
  value,
  onValueChange,
  placeholder,
  className,
  disabled = false,
  fetcher,
  fetching = false,
  onSearch,
  showAllOnEmpty = true,
  maxResultsWhileSearching = 5,
  emptyMessage = "No results found",
}: SearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find selected option
  const selectedOption = options.find((option) => option.value === value);

  // Update display value when value changes
  useEffect(() => {
    if (selectedOption) {
      setDisplayValue(selectedOption.label);
    } else {
      setDisplayValue("");
    }
  }, [selectedOption]);

  // Filter options based on search
  const filteredOptions = searchValue.trim()
    ? options
        .filter((option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase())
        )
        .slice(0, maxResultsWhileSearching)
    : showAllOnEmpty
      ? options
      : [];

  // Handle search with debounce
  const handleSearch = useCallback(
    (query: string) => {
      setSearchValue(query);
      if (onSearch) {
        onSearch(query);
      } else if (fetcher) {
        fetcher(query);
      }
    },
    [onSearch, fetcher]
  );

  // Handle option selection
  const handleSelect = (option: { value: string; label: string }) => {
    onValueChange(option.value);
    setDisplayValue(option.label);
    setSearchValue("");
    setIsOpen(false);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchValue("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className={cn(
          "flex justify-between items-center pr-4 h-16 cursor-pointer",
          className,
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span
          className={cn(
            "flex-1 text-left",
            !displayValue && "text-secondary text-sm"
          )}
        >
          {displayValue || placeholder}
        </span>
        <ChevronDown
          className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")}
        />
      </div>

      {isOpen && !disabled && (
        <div className="top-full left-0 z-50 absolute bg-background shadow-lg mt-1 border border-border rounded-lg w-full max-h-60 overflow-hidden">
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <Search className="w-4 h-4" />
            <Input
              ref={inputRef}
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search..."
              className="bg-transparent p-0 border-none text-sm focus:ring-0"
              autoFocus
            />
            {fetching && <Spinner width={16} />}
          </div>

          <ScrollArea className="pb-5 max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                <div
                  key={option.value}
                  className={cn(
                    "flex justify-between items-center gap-2 hover:bg-tertiarybuttonbg px-3 py-5 cursor-pointer"
                  )}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                  {value === option.value ? <CheckIcon /> : null}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-center text-gray-400 text-sm">
                {fetching ? "Searching..." : emptyMessage}
              </div>
            )}
            <ScrollBar />
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
